// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullRegattaResultTable,
  $FullRegattaResultTableRace,
  $ModelUpsertParams,
} from 'common.data';

// Global Enums
import {
  RegattaResultTableRaceType,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Input,
  Table,
} from 'reactstrap';
import {
  FormDialog,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import moment from 'moment';
import {
  cleanFormValues,
  makeCode,
  numberValue,
} from '@vroskus/library-helpers/dist/browser';
import {
  defaultUpsertItem,
} from '../../../../helpers';

// Components
import RegattaResultTableRaceFields from '../../../fields/RegattaResultTableRaceFields';

// Services
import Services from '../../../../services';

// Types
import type {
  $ActiveRegattaResourceTableRaces,
} from '../../../../types';

type $Props = $Translate & {
  data: Array<$FullRegattaResultTable>;
  onChange: (arg0: $ActiveRegattaResourceTableRaces) => void;
};

type $State = {
  activeRegattaResourceTableRaces: $ActiveRegattaResourceTableRaces;
};

class RegattaResultTableRaceSelectorSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      activeRegattaResourceTableRaces: {
      },
    };
  }

  renderAddRaceButton({
    regattaResultTable,
  }: {
    regattaResultTable: $FullRegattaResultTable;
  }) {
    const {
      t,
    } = this.props;

    const now: moment.Moment = moment();

    const newRegattaResultTableRaceItem = {
      Approved: false,
      Coef: 1,
      Course: null,
      Day: Number(now.format('D')),
      Distance: null,
      Dominant: false,
      Hour: 12,
      Minute: 0,
      Month: Number(now.format('M')),
      Name: '',
      Public: false,
      RegattaResultTableId: regattaResultTable.id,
      Second: 0,
      ShowPlaceColumn: true,
      ShowPointsColumn: false,
      ShowTimeColumn: false,
      Twa: null,
      Tws: null,
      Type: RegattaResultTableRaceType.monotype,
      Undiscard: false,
      Year: Number(now.format('YYYY')),
    };

    return (
      <FormDialog
        fields={(
          <RegattaResultTableRaceFields item={newRegattaResultTableRaceItem} />
        )}
        id={`create-regatta-result-table-race-dialog-${regattaResultTable.id}`}
        labelCloseButton={t('Close')}
        labelSaveButton={t('Save')}
        onAction={(values) => defaultUpsertItem({
          action: async () => {
            const params = cleanFormValues<$ModelUpsertParams<'RegattaResultTableRace'>>({
              ...newRegattaResultTableRaceItem,
              ...values,
              Coef: numberValue(values.Coef),
              Day: numberValue(values.Day),
              Hour: numberValue(values.Hour),
              Minute: numberValue(values.Minute),
              Month: numberValue(values.Month),
              Second: numberValue(values.Second),
              Year: numberValue(values.Year),
            });

            await Services.Api.upsertRegattaResultTableRace(params);
          },
        })}
        subTitle={t('New')}
        title={t('Regatta result table race')}
      >
        <Button
          color={'info'}
          id={`create-regatta-result-table-race-button-${regattaResultTable.id}`}
          size={'sm'}
        >
          {t('Add race')}
        </Button>
      </FormDialog>
    );
  }

  render() {
    const {
      data,
      onChange,
      t,
    } = this.props;
    const {
      activeRegattaResourceTableRaces,
    } = this.state;

    const maxRegattaResultTableRaceQuantity: number = _.max(
      data.map((
        regattaResultTable: $FullRegattaResultTable,
      ) => regattaResultTable.RegattaResultTableRaces.length),
    ) || 0;

    return (
      <>
        <Card className={'RegattaResultTilesSection'}>
          <Table responsive>
            <thead>
              <tr>
                <th>
                  {t('Group')}
                </th>
                {[...Array(maxRegattaResultTableRaceQuantity)].map((k, i: number) => (
                  <th
                    className={'text-center'}
                    key={makeCode(`head${i}`)}
                  >
                    {`${t('Race')} ${i + 1}`}
                  </th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {data.map((regattaResultTable: $FullRegattaResultTable) => (
                <tr key={regattaResultTable.id}>
                  <td>
                    {regattaResultTable.Name}
                  </td>
                  {[...Array(maxRegattaResultTableRaceQuantity)].map((k, i: number) => {
                    const regattaResultTableRace: $FullRegattaResultTableRace | undefined = _.get(
                      regattaResultTable,
                      `RegattaResultTableRaces[${i}]`,
                    );

                    return (
                      <td
                        className={'text-center'}
                        key={makeCode(`${regattaResultTable.id}${i}`)}
                      >
                        {regattaResultTableRace ? (
                          /* eslint-disable-next-line  jsx-a11y/label-has-associated-control */
                          <label
                            className={'checkbox c-checkbox c-checkbox-rounded'}
                            htmlFor={regattaResultTableRace.id}
                            title={regattaResultTableRace.Name}
                          >
                            <Input
                              checked={activeRegattaResourceTableRaces[
                                regattaResultTable.id
                              ] === regattaResultTableRace.id}
                              id={regattaResultTableRace.id}
                              onClick={() => {
                                const currenRaceId: string | undefined = _.get(
                                  activeRegattaResourceTableRaces,
                                  regattaResultTable.id,
                                );

                                if (currenRaceId === regattaResultTableRace.id) {
                                  const value: $ActiveRegattaResourceTableRaces = _.omit(
                                    activeRegattaResourceTableRaces,
                                    regattaResultTable.id,
                                  );

                                  this.setState(
                                    {
                                      activeRegattaResourceTableRaces: value,
                                    },
                                    () => onChange(value),
                                  );
                                } else {
                                  const value: $ActiveRegattaResourceTableRaces = {
                                    ...activeRegattaResourceTableRaces,
                                    [regattaResultTable.id]: regattaResultTableRace.id,
                                  };

                                  this.setState(
                                    {
                                      activeRegattaResourceTableRaces: value,
                                    },
                                    () => onChange(value),
                                  );
                                }
                              }}
                              type={'checkbox'}
                            />
                            <span className={'fa fa-check'} />
                          </label>
                        ) : null}
                      </td>
                    );
                  })}
                  <td className={'text-right py-1'}>
                    {this.renderAddRaceButton({
                      regattaResultTable,
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
        {_.isEmpty(activeRegattaResourceTableRaces) && (
          <Card>
            <div className={'text-muted text-center p-3'}>
              {t('Select the race to input data')}
            </div>
          </Card>
        )}
      </>
    );
  }
}

export default RegattaResultTableRaceSelectorSectionView;
